<template>
    <div class="space-y-4">
        <div>
            <label class="forms__label">
                {{ lang('content::content.widgets.block_editor.block') }}
            </label>
            <AdminSelect
                v-model="blockId"
                :options="blocks"
                :searchable="true"
                :placeholder="lang('content::content.widgets.block_editor.block')"
                @change="onBlockChange"
            />
        </div>

        <div>
            <label class="forms__label">
                {{ lang('content::content.widgets.block_editor.content_item') }}
            </label>
            <AdminSelect
                v-model="contentItemIndex"
                :options="contentItems"
                :searchable="true"
                :disabled="!blockId || loadingItems"
                :placeholder="
                    loadingItems
                        ? lang('general.loading')
                        : lang('content::content.widgets.block_editor.content_item')
                "
                @change="onContentItemChange"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue';
import axios from 'axios';
import { lang } from '@/lang/composable';

interface Block {
    id: number;
    name: string;
}

interface ContentItem {
    id: number;
    name: string;
}

interface FieldDef {
    blocks?: Block[];
    itemsRoute?: string;
    [key: string]: unknown;
}

interface Props {
    modelValue?: {
        block_id?: number | null;
        content_item_index?: number | null;
    } | null;
    field?: FieldDef;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    field: () => ({}),
});

const blocks = computed(() => props.field?.blocks ?? []);
const itemsRoute = computed(() => props.field?.itemsRoute ?? '');

const emit = defineEmits<{
    (event: 'update:modelValue', value: { block_id: number | null; content_item_index: number | null }): void;
}>();

const blockId = ref<number | null>(props.modelValue?.block_id ?? null);
const contentItemIndex = ref<number | null>(props.modelValue?.content_item_index ?? null);
const contentItems = ref<ContentItem[]>([]);
const loadingItems = ref(false);

const loadContentItems = async (id: number) => {
    if (!itemsRoute.value) {
        return;
    }

    loadingItems.value = true;
    contentItems.value = [];

    try {
        const url = itemsRoute.value.replace('__BLOCK_ID__', String(id));
        const { data } = await axios.get(url);
        contentItems.value = data.items || [];
    } catch (error) {
        console.error('Failed to load content items:', error);
        contentItems.value = [];
    } finally {
        loadingItems.value = false;
    }
};

const onBlockChange = (newBlockId: number | null) => {
    blockId.value = newBlockId;
    contentItemIndex.value = null;
    contentItems.value = [];

    if (newBlockId) {
        loadContentItems(newBlockId);
    }

    emitValue();
};

const onContentItemChange = (newIndex: number | null) => {
    contentItemIndex.value = newIndex;
    emitValue();
};

const emitValue = () => {
    emit('update:modelValue', {
        block_id: blockId.value,
        content_item_index: contentItemIndex.value,
    });
};

watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue) {
            if (newValue.block_id !== blockId.value) {
                blockId.value = newValue.block_id ?? null;
                if (blockId.value) {
                    loadContentItems(blockId.value);
                }
            }
            contentItemIndex.value = newValue.content_item_index ?? null;
        }
    },
    { deep: true },
);

onMounted(() => {
    if (blockId.value) {
        loadContentItems(blockId.value);
    }
});
</script>
